import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import LabourSafetySection from './LabourSafetySection';
import { AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import Cookies from 'js-cookie';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { useSmartBack } from '../../../shared/navigation';
import Loader from '../../../shared/components/ui/Loader';
import Modal from '../../../shared/components/ui/Modal';
import { Callout, StatusPill } from '../../../shared/components/ui/primitives';
import PunchLocationMap from '../../../shared/components/ui/PunchLocationMap';
import { useProjectsList } from '../../../../contexts/ProjectsContext';
import {
  createLabour,
  getLabourDetail,
  getLabourPhotos,
  updateLabour,
  settleLabour,
  redactLabourPii,
  getServiceProviders,
  uploadFilesToS3,
  uploadLabourIdProof,
  getLabourRateRequests,
  createLabourRateRequest,
  cancelLabourRateRequest
} from '../../services/api/implementation/impl';
import { useApproverAccess } from '../../hooks/useApproverAccess';

const SKILLS = ['SKILLED', 'SEMI_SKILLED', 'UNSKILLED'];

/**
 * The trades the system recognises, in the backend's own spelling.
 *
 * This was a free-text box. Both the band-rate and the minimum-wage lookup
 * match on this exact string, so "mason" or "Mistri" did not fail loudly — the
 * workman simply matched no rate and no wage floor, and nothing said so.
 */
const WORK_TYPES = [
  'MASON',
  'HELPER',
  'CARPENTER',
  'BAR_BENDER',
  'ELECTRICIAN',
  'PLUMBER',
  'PAINTER',
  'WELDER',
  'FITTER',
  'OPERATOR',
  'SUPERVISOR',
  'OTHER'
];
const WAGE_PERIODS = ['DAILY', 'WEEKLY', 'MONTHLY'];
const ACCOUNT_TYPES = ['SAVINGS', 'CURRENT', 'SALARY', 'JAN_DHAN'];

/**
 * The banks a workman is likely to hold an account with.
 *
 * Common ones first, deliberately, rather than alphabetical: on a site the
 * account is nearly always with one of a handful, and making somebody scroll
 * past forty names to reach State Bank is how a free-text box gets asked for
 * again. `Other` stays at the bottom because a district cooperative or a
 * regional rural bank will never be on any list, and without an escape hatch
 * people pick the wrong bank to get past the field.
 */
const COMMON_BANKS = [
  'State Bank of India',
  'HDFC Bank',
  'ICICI Bank',
  'Punjab National Bank',
  'Bank of Baroda',
  'Axis Bank',
  'Canara Bank',
  'Union Bank of India',
  'Kotak Mahindra Bank',
  'Bank of India'
];

const OTHER_BANKS = [
  'Central Bank of India',
  'Indian Bank',
  'Indian Overseas Bank',
  'UCO Bank',
  'Bank of Maharashtra',
  'Punjab & Sind Bank',
  'IDBI Bank',
  'Yes Bank',
  'IndusInd Bank',
  'Federal Bank',
  'IDFC First Bank',
  'RBL Bank',
  'Bandhan Bank',
  'South Indian Bank',
  'Karnataka Bank',
  'Karur Vysya Bank',
  'City Union Bank',
  'DCB Bank',
  'Jammu & Kashmir Bank',
  'Tamilnad Mercantile Bank',
  'CSB Bank',
  'Dhanlaxmi Bank',
  'AU Small Finance Bank',
  'Ujjivan Small Finance Bank',
  'Equitas Small Finance Bank',
  'Jana Small Finance Bank',
  'Suryoday Small Finance Bank',
  'ESAF Small Finance Bank',
  'Utkarsh Small Finance Bank',
  'India Post Payments Bank',
  'Airtel Payments Bank',
  'Regional Rural Bank',
  'District Cooperative Bank',
  'Other'
];
/**
 * Everything past the common ten, in the order somebody scanning a list
 * expects: A to Z, with the escape hatch last rather than sorted into the O's.
 */
const OTHER_BANKS_SORTED = [
  ...OTHER_BANKS.filter((b) => b !== 'Other').sort((a, b) => a.localeCompare(b)),
  'Other'
];

/**
 * Why somebody came off the roster.
 *
 * Free text produced "left", "Left", "gaya" and blank in the same column, and
 * the one reason a builder actually wants counted — a move to another of his
 * own sites — was indistinguishable from a man who walked off.
 */
const TRANSFER_REASON = 'TRANSFER_TO_ANOTHER_SITE';
const EXIT_REASONS = [
  'WORK_COMPLETED',
  TRANSFER_REASON,
  'ABSCONDED',
  'TERMINATED',
  'PERSONAL',
  'OTHER'
];

const ID_PROOF_TYPES = [
  'AADHAAR',
  'VOTER_ID',
  'DRIVING_LICENCE',
  'PAN',
  'RATION_CARD',
  'OTHER'
];

/**
 * Free text here had already produced Male, male, Female and female in the same
 * column. The CLRA register groups by this, so four spellings of two genders
 * split one row into two and the totals stop agreeing with the headcount.
 */
const GENDERS = ['Male', 'Female', 'Other'];

/**
 * The server lists missing register fields by column name. Shown as the labels
 * on this form, so the reader can find the box — "nature_of_work" matched
 * nothing on screen.
 */
const STATUTORY_LABELS = {
  father_or_husband_name: 'Father / husband name',
  date_of_birth: 'Date of birth',
  permanent_address: 'Permanent address',
  nature_of_work: 'Trade / work type',
  date_of_joining: 'Date of joining',
  wage_period: 'Wage period',
  id_proof_number: 'ID proof number'
};

/**
 * Add or complete a workman's record.
 *
 * Site capture only collects name/trade/rate. Father's name, address, ID and
 * the rest live here so the CLRA register can actually be produced.
 */
const LabourDetail = () => {
  const { onBack } = useSmartBack({ fallback: '/builder/operations/labour' });
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { projectsForDropdown } = useProjectsList();

  // An existing workman's day rate changes only through a rate change request,
  // approved by the account owner or the assigned rate approver. The owner
  // still edits it directly. Until /approvals/me answers (or if it fails) the
  // field behaves as before and the server has the last word.
  const access = useApproverAccess();
  const rateLocked = !isNew && access.loaded && !access.isOwner;
  const [requestingRate, setRequestingRate] = useState(false);

  const {
    data: worker,
    isFetching,
    error: loadError
  } = useQuery({
    queryKey: ['labour', id],
    queryFn: () => getLabourDetail(id),
    enabled: !isNew,
    // A 404 will not become a 200 by asking again. Retrying it only kept the
    // spinner up for several seconds before the page went blank for good.
    retry: (count, err) => err?.response?.status !== 404 && count < 2
  });

  /**
   * Contractors this builder engages.
   *
   * Only builder staff pick one. A contractor logging in has their own scope
   * and the workmen they add are theirs by definition — offering them a picker
   * would let them put a workman on somebody else's books.
   */
  const contractors = useQuery({
    queryKey: ['serviceProvidersForLabour'],
    queryFn: getServiceProviders,
    staleTime: 10 * 60 * 1000
  });
  const contractorOptions = useMemo(() => {
    const raw = contractors.data?.data ?? contractors.data ?? [];
    const rows = Array.isArray(raw) ? raw : raw.rows ?? [];
    return rows
      .filter((sp) => sp?.id)
      .map((sp) => ({
        id: String(sp.id),
        // The name lives on the linked user. company_name is blank for most
        // contractors on record, so reading it first shows "Contractor #52"
        // where the builder expects "Kumar Gaurav".
        label: sp.user?.name || sp.company_name || `Contractor #${sp.id}`
      }));
  }, [contractors.data]);

  const [form, setForm] = useState(() => emptyForm());
  const [uploadingProof, setUploadingProof] = useState(false);
  const [readingId, setReadingId] = useState(false);
  /**
   * What the card said, waiting for somebody to agree with it.
   *
   * Held here rather than written into the form, because these fields go onto
   * a statutory register and a model's reading of a blurred Aadhaar is not a
   * good enough reason to put a father's name on Form XIII. Nothing moves into
   * the form until the button below is pressed.
   */
  const [suggestion, setSuggestion] = useState(null);

  const readIdProof = async (file) => {
    if (!file || isNew) return;
    setReadingId(true);
    setSuggestion(null);
    try {
      const uploaded = await uploadFilesToS3([file]);
      const first = uploaded?.[0];
      if (!first?.url) throw new Error('No file came back');

      const result = await uploadLabourIdProof(id, {
        url: first.url,
        name: first.name || file.name,
        mime_type: file.type,
        id_proof_type: form.id_proof_type || undefined
      });

      queryClient.invalidateQueries({ queryKey: ['labourPhotos', worker?.id] });

      const found = result?.suggestion ?? {};
      if (Object.keys(found).length === 0) {
        toast(
          'Filed, but nothing could be read off it. Type the details in as usual.',
          { icon: 'ℹ️' }
        );
      } else {
        setSuggestion(found);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not read that document');
    } finally {
      setReadingId(false);
    }
  };

  /** Take the reading into the form. Still nothing saved until Save. */
  const acceptSuggestion = () => {
    if (!suggestion) return;
    setForm((f) => ({
      ...f,
      ...Object.fromEntries(
        Object.entries(suggestion).filter(
          ([key, value]) =>
            value != null &&
            value !== '' &&
            key !== 'id_proof_masked' &&
            Object.prototype.hasOwnProperty.call(f, key)
        )
      )
    }));
    setSuggestion(null);
    toast.success('Filled in. Check it, then save.');
  };

  /**
   * The passbook or cheque, uploaded rather than pasted.
   *
   * This was a box asking for "a link to the image" — a supervisor holding a
   * photo on a phone has no link to give, so the field stayed empty and the
   * account number stayed unverifiable.
   */
  const uploadBankProof = async (file) => {
    if (!file) return;
    setUploadingProof(true);
    try {
      const uploaded = await uploadFilesToS3([file]);
      const first = uploaded?.[0];
      if (!first?.url) throw new Error('No file came back');
      setForm((f) => ({
        ...f,
        bank_proof_url: first.url,
        bank_proof_name: first.name || file.name
      }));
      toast.success('Uploaded');
    } catch (err) {
      toast.error('Could not upload that file');
    } finally {
      setUploadingProof(false);
    }
  };

  useEffect(() => {
    if (worker) setForm(toForm(worker));
  }, [worker]);

  const save = useMutation({
    mutationFn: (payload) =>
      isNew ? createLabour(payload) : updateLabour(id, payload),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['labourRegister'] });
      queryClient.invalidateQueries({ queryKey: ['labour', id] });

      // A name that corresponds to somebody already on the roster is the most
      // common fraud in site labour. The record IS created — refusing the
      // second Ram Kumar would push a supervisor to misspell the name and
      // destroy the only signal there was — so this is a long toast naming who
      // the match is, not a blocked save.
      const duplicates = saved?.possible_duplicates ?? [];
      if (isNew && duplicates.length) {
        toast(
          `Added. Already on the roster and similar: ${duplicates
            .map((d) => `${d.name} (${d.reasons.join(', ')})`)
            .join('; ')}`,
          { duration: 12000, icon: '⚠️' }
        );
      } else {
        toast.success(isNew ? 'Labour added' : 'Saved');
      }

      const nextId = saved?.id ?? id;
      if (isNew && nextId) navigate(`/builder/operations/labour/${nextId}`, { replace: true });
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not save')
  });

  /**
   * Off the roster without losing the record.
   *
   * Exit is a dated, statutory event with a settlement behind it. This is the
   * smaller thing people actually asked for: a man who has stopped coming and
   * should not be on tomorrow's muster, put back the day he returns. The
   * backend refuses it while money is owed, and says so.
   */
  const setActive = useMutation({
    mutationFn: (next) => updateLabour(id, { is_active: next }),
    onSuccess: (_saved, next) => {
      queryClient.invalidateQueries({ queryKey: ['labour', id] });
      queryClient.invalidateQueries({ queryKey: ['labourRegister'] });
      toast.success(next ? 'Back on the roster' : 'Taken off the roster');
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not change that')
  });

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const toggleProject = (projectId) => {
    const next = new Set(form.projectIds);
    if (next.has(projectId)) next.delete(projectId);
    else next.add(projectId);
    setForm({ ...form, projectIds: [...next] });
  };

  /**
   * A transfer names where he went, so the receiving site's register shows him
   * rather than nobody's.
   */
  const projectIdsOnSave = () => {
    if (form.reason_for_leaving !== TRANSFER_REASON) return form.projectIds;
    const raw = form.transfer_to_project_id;
    if (!raw) return form.projectIds;
    const target =
      (projectsForDropdown ?? []).find((p) => String(p.id) === String(raw))?.id ?? raw;
    if (form.projectIds.includes(target)) return form.projectIds;
    return [...form.projectIds, target];
  };

  const submit = () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (form.reason_for_leaving === TRANSFER_REASON && !form.transfer_to_project_id) {
      toast.error('Name the site he was transferred to');
      return;
    }
    // Only when it actually changed. An existing record loads both boxes from
    // the same stored value, so opening a workman to edit his trade does not
    // demand his account number be typed out again.
    const accountTouched =
      (form.bank_account || '') !== (worker?.bank_account || '');
    if (
      accountTouched &&
      form.bank_account &&
      form.bank_account !== form.bank_account_confirm
    ) {
      toast.error('The two account numbers do not match');
      return;
    }
    if (form.phone_number && form.phone_number.length !== 10) {
      toast.error('Phone number must be 10 digits');
      return;
    }
    save.mutate({
      name: form.name.trim(),
      phone_number: form.phone_number || undefined,
      trade: form.trade || undefined,
      skill_level: form.skill_level || undefined,
      // Empty means in-house. Sent as null rather than omitted so clearing it
      // moves a workman back off a contractor instead of silently doing nothing.
      sp_id: form.sp_id ? Number(form.sp_id) : null,
      // Not sent when the rate is locked: it changes through a request, and
      // sending it would only earn a refusal for the whole save.
      ratePerDay: rateLocked
        ? undefined
        : form.ratePerDay
          ? Number(form.ratePerDay)
          : undefined,
      overtime_rate_per_hour: form.overtime_rate_per_hour
        ? Number(form.overtime_rate_per_hour)
        : undefined,
      gender: form.gender || undefined,
      workingHours: form.workingHours ? Number(form.workingHours) : undefined,
      father_or_husband_name: form.father_or_husband_name || undefined,
      date_of_birth: form.date_of_birth || undefined,
      permanent_address: form.permanent_address || undefined,
      present_address: form.present_address || undefined,
      date_of_joining: form.date_of_joining || undefined,
      date_of_exit: form.date_of_exit || undefined,
      reason_for_leaving: form.reason_for_leaving || undefined,
      wage_period: form.wage_period || undefined,
      id_proof_type: form.id_proof_type || undefined,
      id_proof_number: form.id_proof_number || undefined,
      uan: form.uan || undefined,
      esic_no: form.esic_no || undefined,
      bank_account: form.bank_account || undefined,
      ifsc: form.ifsc || undefined,
      bank_account_name: form.bank_account_name || undefined,
      bank_account_type: form.bank_account_type || undefined,
      bank_name: form.bank_name || undefined,
      bank_proof_url: form.bank_proof_url || undefined,
      bank_proof_name: form.bank_proof_name || undefined,
      projectIds: projectIdsOnSave()
    });
  };

  // Loading ended without him. This used to fall through to the spinner and
  // stay there, which read as "the labour was not added" when it had been.
  if (!isNew && !worker && !isFetching && loadError) {
    const notFound = loadError?.response?.status === 404;
    return (
      <Layout>
        <Header backArrow onBack={onBack} data={{ name: 'Labour' }} />
        <div className="p-4">
          <div className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-700 space-y-3">
            <p className="font-medium text-gray-900">
              {notFound
                ? 'You cannot open this labour record'
                : 'Could not load this labour record'}
            </p>
            <p>
              {notFound
                ? 'He is not on any site you are assigned to, or the record no longer exists. If he should be on your site, ask an admin to add him to it.'
                : loadError?.response?.data?.message || loadError?.message || 'Try again in a moment.'}
            </p>
            <button
              type="button"
              onClick={() => navigate('/builder/operations/labour')}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50"
            >
              Back to labour
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  if (!isNew && (isFetching || !worker)) {
    return (
      <Layout>
        <Header backArrow onBack={onBack} data={{ name: 'Labour' }} />
        <Loader />
      </Layout>
    );
  }

  const missing = worker?.missing_statutory_fields ?? [];

  return (
    <Layout>
      <Header backArrow onBack={onBack} data={{ name: isNew ? 'Add labour' : worker.name }} />
      {/* Full width, like every other Labour page. This one carried a
          max-width nothing else in the module has, so the form sat in the
          left half of a wide screen with the right half empty. */}
      <div className="p-4 space-y-4">
        {/* Who this is, before what is stored about him. The photograph was at
            the foot of the page under three other cards, which is no use to
            somebody checking they have opened the right Ram Kumar. */}
        {!isNew && (
          <LabourIdentity
            worker={worker}
            contractorLabel={
              contractorOptions.find((o) => o.id === String(worker.sp_id))?.label
            }
            onSetActive={(next) => setActive.mutate(next)}
            busy={setActive.isPending}
          />
        )}

        {/* The registers this system prints go to an inspector. Whoever types
            the rate should see the floor before that happens, not after. */}
        {!isNew && worker.minimum_wage?.compliant === false && (
          <div className="flex items-start gap-2 rounded-control border border-danger-border bg-danger-bg px-3 py-2 text-xs text-danger-fg">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>
              {worker.minimum_wage.message}
              {worker.minimum_wage.notification_reference
                ? ` (${worker.minimum_wage.notification_reference})`
                : ''}{' '}
              This is not blocked — the day still records — but it will appear on
              the statutory register exactly as it stands.
            </span>
          </div>
        )}

        {!isNew && missing.length > 0 && (
          <div className="flex items-start gap-2 rounded-control border border-warning-border bg-warning-bg px-3 py-2 text-xs text-warning-fg">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>
              Missing for the statutory register:{' '}
              {missing.map((key) => STATUTORY_LABELS[key] ?? key).join(', ')}.
            </span>
          </div>
        )}

        {suggestion && (
          <IdProofSuggestion
            suggestion={suggestion}
            onAccept={acceptSuggestion}
            onDiscard={() => setSuggestion(null)}
          />
        )}

        <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
          <Section title="On site">
            <Grid>
              <Input label="Name" value={form.name} onChange={set('name')} />
              <Input
                label="Phone"
                type="tel"
                inputMode="numeric"
                placeholder="10-digit mobile number"
                value={form.phone_number}
                onChange={(e) =>
                  setForm((f) => ({ ...f, phone_number: phoneDigits(e.target.value) }))
                }
              />
              <ContractorSelect
                value={form.sp_id}
                options={contractorOptions}
                locked={!isNew && Boolean(worker?.has_wage_entries)}
                onChange={set('sp_id')}
              />
              {/* Trade is also what the statutory register prints as "Nature
                  of employment". There used to be a free-text "Nature of work"
                  box beside it doing the same job, which is how one workman
                  reached Form XIII as a Mason and the next as a mistri. */}
              <Select
                label="Trade / work type"
                value={form.trade}
                onChange={set('trade')}
                // A workman recorded before this was a list may hold something
                // off it. Keep their value in the options so the field shows
                // what is actually stored instead of rendering blank and
                // looking like the trade was lost.
                options={
                  form.trade && !WORK_TYPES.includes(form.trade)
                    ? [...WORK_TYPES, form.trade]
                    : WORK_TYPES
                }
              />
              <Select label="Skill" value={form.skill_level} onChange={set('skill_level')} options={SKILLS} />
              <Select
                label="Gender"
                value={form.gender}
                onChange={set('gender')}
                // Anything already stored in another spelling stays selectable,
                // so an existing record shows what it holds instead of looking
                // blank — but everything saved from here on is one of the three.
                options={
                  form.gender && !GENDERS.includes(form.gender)
                    ? [...GENDERS, form.gender]
                    : GENDERS
                }
              />
              {rateLocked ? (
                <div>
                  <Readout label="Rate / day" value={worker?.ratePerDay ? money(worker.ratePerDay) : ''} />
                  <button
                    type="button"
                    onClick={() => setRequestingRate(true)}
                    className="mt-1 text-xs font-medium text-brand-700 underline">
                    Request rate change
                  </button>
                </div>
              ) : (
                <Input label="Rate / day" type="number" value={form.ratePerDay} onChange={set('ratePerDay')} />
              )}
              <Input
                label="Standard hours"
                type="number"
                value={form.workingHours}
                onChange={set('workingHours')}
              />
              {/* Not a second stored rate — the day rate divided by the hours
                  it is meant to cover. A half day, a short shift and an
                  overtime hour are all argued in rupees per hour on site, and
                  doing that division in your head over a muster is how the
                  wrong figure gets agreed. */}
              <Readout
                label="Rate / hour"
                value={perHourRate(form.ratePerDay, form.workingHours)}
                hint="Day rate ÷ standard hours."
              />
              <Input
                label="Overtime / hour"
                type="number"
                value={form.overtime_rate_per_hour}
                onChange={set('overtime_rate_per_hour')}
              />
            </Grid>
          </Section>

          {!isNew && (
            <RateRequests
              worker={worker}
              canRequest={rateLocked}
              modalOpen={requestingRate}
              setModalOpen={setRequestingRate}
            />
          )}

          <Section title="Statutory" hint="Needed for Form XIII. Not collected on site.">
            <Grid>
              <Input
                label="Father / husband name"
                value={form.father_or_husband_name}
                onChange={set('father_or_husband_name')}
              />
              <Input
                label="Date of birth"
                type="date"
                value={form.date_of_birth}
                onChange={set('date_of_birth')}
              />
              <Input
                label="Date of joining"
                type="date"
                value={form.date_of_joining}
                onChange={set('date_of_joining')}
              />
              <Select
                label="Wage period"
                value={form.wage_period}
                onChange={set('wage_period')}
                options={WAGE_PERIODS}
              />
              <Select
                label="ID proof type"
                value={form.id_proof_type}
                onChange={set('id_proof_type')}
                options={
                  form.id_proof_type && !ID_PROOF_TYPES.includes(form.id_proof_type)
                    ? [...ID_PROOF_TYPES, form.id_proof_type]
                    : ID_PROOF_TYPES
                }
              />
              <Input
                label="ID proof number"
                value={form.id_proof_number}
                onChange={set('id_proof_number')}
              />
              {/* The supervisor photographs the card and the details come off
                  it. Only on a saved workman — there has to be somebody to
                  file the image against. */}
              {!isNew && (
                <IdProofCapture busy={readingId} onPick={readIdProof} />
              )}
              <Input label="UAN" value={form.uan} onChange={set('uan')} />
              <Input label="ESIC no" value={form.esic_no} onChange={set('esic_no')} />
              <Input
                label="Permanent address"
                value={form.permanent_address}
                onChange={set('permanent_address')}
                className="sm:col-span-2"
              />
              <Input
                label="Present address"
                value={form.present_address}
                onChange={set('present_address')}
                className="sm:col-span-2"
              />
            </Grid>
          </Section>

          <Section
            title="Bank"
            hint="Where the wages go. A transfer usually fails because the holder's name does not match the number."
          >
            <Grid>
              <Input
                label="Account holder"
                value={form.bank_account_name}
                onChange={set('bank_account_name')}
              />
              {/* Typed once and nobody can check it: the number is masked, so
                  the second entry is a real re-type rather than a glance at
                  what is already on screen. A wrong digit here is a transfer
                  that bounces a week later with nothing to say why. */}
              {/* Masked, but not a password box. A password box makes the
                  browser treat this page as a login form, so it offered every
                  saved sign-in in the text boxes above it (Father / husband
                  name showed the supervisor's email) and could fill a saved
                  password in here as the account number. */}
              <Input
                label="Account number"
                masked
                inputMode="numeric"
                value={form.bank_account}
                onChange={set('bank_account')}
              />
              <Input
                label="Confirm account number"
                value={form.bank_account_confirm}
                onChange={set('bank_account_confirm')}
              />
              <Input label="IFSC" value={form.ifsc} onChange={set('ifsc')} />
              <Select
                label="Account type"
                value={form.bank_account_type}
                onChange={set('bank_account_type')}
                options={ACCOUNT_TYPES}
              />
              <BankSelect
                value={form.bank_name}
                onChange={set('bank_name')}
              />
              <FileField
                label="Passbook or cheque"
                name={form.bank_proof_name}
                url={form.bank_proof_url}
                uploading={uploadingProof}
                onPick={uploadBankProof}
                onClear={() =>
                  setForm((f) => ({ ...f, bank_proof_url: '', bank_proof_name: '' }))
                }
              />
            </Grid>
          </Section>

          <Section
            title="Exit"
            hint="Saving an exit date while a balance is open will be refused. Use Full & final below to settle first."
          >
            <Grid>
              <Input
                label="Date of exit"
                type="date"
                value={form.date_of_exit}
                onChange={set('date_of_exit')}
              />
              <Select
                label="Reason for leaving"
                value={form.reason_for_leaving}
                onChange={set('reason_for_leaving')}
                // A reason typed by hand cannot be counted. Anything already
                // stored stays selectable so an old record shows what it holds.
                options={
                  form.reason_for_leaving &&
                  !EXIT_REASONS.includes(form.reason_for_leaving)
                    ? [...EXIT_REASONS, form.reason_for_leaving]
                    : EXIT_REASONS
                }
              />
              {/* Moving to another site of the same builder is the commonest
                  exit there is, and it is not really an exit — the workman is
                  still on the books. Naming the site he goes to puts him on
                  that register instead of leaving him off every one. */}
              {form.reason_for_leaving === TRANSFER_REASON && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Transferred to
                  </label>
                  <select
                    value={form.transfer_to_project_id}
                    onChange={set('transfer_to_project_id')}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                  >
                    <option value="">—</option>
                    {(projectsForDropdown ?? [])
                      .filter((p) => !form.projectIds.includes(p.id))
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.label}
                        </option>
                      ))}
                  </select>
                  <p className="mt-1 text-[11px] text-gray-500">
                    He is added to that site&apos;s register on save.
                  </p>
                </div>
              )}
            </Grid>
          </Section>

          <Section title="Sites">
            <div className="flex flex-wrap gap-2">
              {(projectsForDropdown ?? []).length === 0 ? (
                <p className="text-sm text-gray-500">No projects loaded.</p>
              ) : (
                projectsForDropdown.map((project) => (
                  <label
                    key={project.id}
                    className={`text-sm border rounded-lg px-3 py-1.5 cursor-pointer ${
                      form.projectIds.includes(project.id)
                        ? 'border-brand-700 bg-brand-50 text-brand-900'
                        : 'border-gray-200 text-gray-700'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={form.projectIds.includes(project.id)}
                      onChange={() => toggleProject(project.id)}
                    />
                    {project.label}
                  </label>
                ))
              )}
            </div>
          </Section>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => navigate('/builder/operations/labour')}
              className="px-4 py-2 rounded border border-gray-300 text-sm"
            >
              Back
            </button>
            <button
              type="button"
              disabled={save.isPending}
              onClick={submit}
              className="px-4 py-2 rounded bg-brand-700 text-white text-sm disabled:opacity-50"
            >
              {save.isPending ? 'Saving…' : isNew ? 'Add labour' : 'Save'}
            </button>
          </div>
        </div>

        {!isNew && <LabourSafetySection labourId={worker.id} />}

        {!isNew && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
            <LabourPhotos labourId={worker.id} photos={worker.photos ?? []} />
            <FullAndFinal worker={worker} />
            <RecentDays days={worker.recent_attendance ?? []} />
          </div>
        )}
      </div>
    </Layout>
  );
};

const CERT_KINDS = ['HEIGHT', 'CONFINED_SPACE', 'HOT_WORK', 'SCAFFOLDING', 'FIRST_AID', 'OTHER'];


const FullAndFinal = ({ worker }) => {
  const queryClient = useQueryClient();
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState('');
  const [writeOff, setWriteOff] = useState(false);
  const settle = useMutation({
    mutationFn: () =>
      settleLabour(worker.id, {
        date_of_exit: date,
        reason_for_leaving: reason,
        payment_amount: amount ? Number(amount) : undefined,
        write_off: writeOff
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['labour', String(worker.id)] });
      toast.success('Settled and marked exited');
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not settle')
  });
  const redact = useMutation({
    mutationFn: () => redactLabourPii(worker.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['labour', String(worker.id)] });
      toast.success('Identity documents stripped');
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not redact')
  });

  if (worker.pii_redacted_at) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
        Identity documents were stripped on {String(worker.pii_redacted_at).slice(0, 10)}.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
      <div className="text-sm font-semibold text-gray-800">Full &amp; final</div>
      <p className="text-xs text-gray-500">
        Pays or writes off whatever is outstanding, then marks the workman
        exited. A save on the form above will refuse an exit while a balance remains.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Input label="Exit date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        <Input label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
        <Input
          label="Final payment (optional)"
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={writeOff}
          onChange={(e) => setWriteOff(e.target.checked)}
        />
        Write off any remainder
      </label>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={settle.isPending || !date}
          onClick={() => settle.mutate()}
          className="rounded bg-brand-700 px-3 py-2 text-sm text-white disabled:opacity-50"
        >
          {settle.isPending ? 'Settling…' : 'Settle and exit'}
        </button>
        {!worker.is_active && (
          <button
            type="button"
            disabled={redact.isPending}
            onClick={() => redact.mutate()}
            className="rounded border border-gray-300 px-3 py-2 text-sm"
          >
            Strip identity documents
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Photographs of the workman, taken on a supervisor's phone.
 *
 * Read-only here. Uploading happens on site, where the person and the camera
 * are; a web upload would be somebody attaching a file they were sent, which is
 * a different and much weaker claim about what the picture shows.
 */
const LabourPhotos = ({ labourId, photos }) => {
  const { data } = useQuery({
    queryKey: ['labourPhotos', labourId],
    queryFn: () => getLabourPhotos(labourId),
    enabled: Boolean(labourId),
    initialData: photos.length ? { data: photos } : undefined
  });

  const rows = data?.data ?? photos ?? [];

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="text-sm font-semibold text-gray-800">Photographs</div>
      <p className="mb-3 text-xs text-gray-500">
        Added from the site engineer&apos;s phone.
      </p>

      {rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-gray-500">
          No photographs yet. A supervisor can add them from the app.
        </p>
      ) : (
        <div className="flex flex-wrap gap-3">
          {rows.map((photo) => (
            <a
              key={photo.id}
              href={photo.url}
              target="_blank"
              rel="noreferrer"
              className="group relative"
            >
              <img
                src={photo.url}
                alt={photo.name ?? 'Labour photograph'}
                className="h-24 w-24 rounded-lg border border-gray-200 object-cover"
              />
              <span className="absolute inset-x-0 bottom-0 rounded-b-lg bg-black/55 px-1 py-0.5 text-center text-[10px] text-white opacity-0 group-hover:opacity-100">
                {titleCaseKind(photo.kind)}
              </span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * The last month of days, with where each punch was recorded.
 *
 * THE COORDINATE IS THE SUPERVISOR'S, not the workman's — labour do not carry
 * phones. The caption says so on every row, because a map pin implies a
 * precision about who was standing where that this data does not support, and
 * the difference has wage and compliance consequences.
 */
const RecentDays = ({ days }) => {
  const [open, setOpen] = useState(null);

  if (!days.length) {
    return null;
  }

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="border-b border-gray-100 px-4 py-3 text-sm font-semibold text-gray-800">
        Recent days
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Date</th>
              <th className="px-4 py-2 text-left font-medium">Status</th>
              <th className="px-4 py-2 text-right font-medium">Hours</th>
              <th className="px-4 py-2 text-right font-medium">Break</th>
              <th className="px-4 py-2 text-left font-medium">Recorded at</th>
            </tr>
          </thead>
          <tbody>
            {days.map((day) => (
              <React.Fragment key={day.id}>
                <tr className="border-t border-gray-100">
                  <td className="whitespace-nowrap px-4 py-2">{dayLabel(day.date)}</td>
                  <td className="px-4 py-2">
                    <span className="text-xs font-medium text-gray-700">
                      {(day.declared_status || day.status || '—')
                        .toString()
                        .replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {Number(day.workingHours || 0)}
                    {Number(day.extraWorkingHours) > 0 && (
                      <span className="text-warning-fg">
                        {' '}
                        + {Number(day.extraWorkingHours)} OT
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-500">
                    {day.break_minutes ? `${day.break_minutes}m` : '—'}
                  </td>
                  <td className="px-4 py-2">
                    {day.punch_in_lat ? (
                      <button
                        type="button"
                        onClick={() => setOpen(open === day.id ? null : day.id)}
                        className="text-xs text-brand-700 hover:underline"
                      >
                        {open === day.id ? 'Hide map' : 'Show map'}
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400">Not recorded</span>
                    )}
                  </td>
                </tr>
                {open === day.id && day.punch_in_lat && (
                  <tr className="border-t border-gray-100 bg-gray-50">
                    <td colSpan={5} className="px-4 py-3">
                      <PunchLocationMap
                        height={220}
                        points={[
                          {
                            lat: day.punch_in_lat,
                            lng: day.punch_in_lng,
                            accuracy_m: day.punch_in_accuracy_m,
                            distance_m: day.punch_in_distance_m,
                            label: 'Punch in',
                            kind: 'IN'
                          },
                          ...(day.punch_out_lat
                            ? [
                                {
                                  lat: day.punch_out_lat,
                                  lng: day.punch_out_lng,
                                  accuracy_m: day.punch_out_accuracy_m,
                                  label: 'Punch out',
                                  kind: 'OUT'
                                }
                              ]
                            : [])
                        ]}
                        caption="This is where the supervisor was standing when they marked the muster. It is not evidence of where the workman was."
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

function dayLabel(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: '2-digit'
  });
}

function titleCaseKind(value) {
  if (!value) return 'Photo';
  return value.charAt(0) + value.slice(1).toLowerCase().replace('_', ' ');
}

/**
 * The day rate expressed by the hour.
 *
 * Eight hours when nothing is set, which is the standard day the rest of the
 * module assumes, so the figure is never silently divided by zero or blank.
 */
function perHourRate(ratePerDay, workingHours) {
  const rate = Number(ratePerDay);
  const hours = Number(workingHours) || 8;
  if (!rate || !Number.isFinite(rate) || hours <= 0) return '';
  return `₹${(rate / hours).toFixed(2)}`;
}

/**
 * A workman's day rate changes through a request an approver signs off, from a
 * start date up to 60 days back. Shows what is pending, lets the requester
 * withdraw it, and keeps the last few decisions in view.
 */
const RateRequests = ({ worker, canRequest, modalOpen, setModalOpen }) => {
  const queryClient = useQueryClient();
  const userId = Number(Cookies.get('userId'));

  const requests = useQuery({
    queryKey: ['labourRateRequests', worker.id],
    queryFn: () => getLabourRateRequests(worker.id)
  });
  const rows = Array.isArray(requests.data) ? requests.data : [];
  const latest = rows[0];
  const pending = latest?.status === 'PENDING' ? latest : null;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['labourRateRequests', worker.id] });
    queryClient.invalidateQueries({ queryKey: ['labour', String(worker.id)] });
    queryClient.invalidateQueries({ queryKey: ['approvalsInbox'] });
  };

  const create = useMutation({
    mutationFn: (payload) => createLabourRateRequest(worker.id, payload),
    onSuccess: () => {
      refresh();
      setModalOpen(false);
      toast.success('Rate change sent for approval');
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not send the request')
  });

  const cancel = useMutation({
    mutationFn: (requestId) => cancelLabourRateRequest(requestId),
    onSuccess: () => {
      refresh();
      toast.success('Request withdrawn');
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not withdraw the request')
  });

  if (!rows.length && !modalOpen) return null;

  return (
    <Section title="Day rate changes">
      <div className="space-y-2">
        {pending && (
          <Callout tone="warning">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>
                {money(pending.requested_rate)} a day requested from{' '}
                {dayMonth(pending.effective_from)} by {pending.requested_by_name || 'someone'} —
                waiting for approval
              </span>
              {Number(pending.requested_by) === userId && (
                <button
                  type="button"
                  disabled={cancel.isPending}
                  onClick={() => cancel.mutate(pending.id)}
                  className="rounded border border-warning-border px-2 py-0.5 font-medium disabled:opacity-50">
                  {cancel.isPending ? 'Withdrawing…' : 'Withdraw'}
                </button>
              )}
            </div>
          </Callout>
        )}

        {rows.length > 0 && (
          <div>
            <div className="mb-1 text-xs font-medium text-gray-600">Rate history</div>
            <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
              {rows.slice(0, 5).map((r) => (
                <li key={r.id} className="flex flex-wrap items-start justify-between gap-2 px-3 py-2 text-xs">
                  <div>
                    <div className="text-gray-800">
                      {money(r.current_rate)} → <span className="font-semibold">{money(r.requested_rate)}</span>{' '}
                      from {dayMonth(r.effective_from)}
                    </div>
                    <div className="text-gray-500">
                      {r.requested_by_name || '—'}
                      {r.reason ? ` · ${r.reason}` : ''}
                    </div>
                    {r.decision_note && (
                      <div className="text-gray-500">
                        {r.decided_by_name ? `${r.decided_by_name}: ` : ''}
                        {r.decision_note}
                      </div>
                    )}
                  </div>
                  <StatusPill status={r.status} dense />
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {modalOpen && canRequest && (
        <RateRequestModal
          currentRate={worker.ratePerDay}
          busy={create.isPending}
          blocked={Boolean(pending)}
          onClose={() => setModalOpen(false)}
          onSubmit={(payload) => create.mutate(payload)}
        />
      )}
    </Section>
  );
};

const RateRequestModal = ({ currentRate, busy, blocked, onClose, onSubmit }) => {
  const today = isoDay(new Date());
  const earliest = isoDay(new Date(Date.now() - 60 * 24 * 60 * 60 * 1000));
  const [rate, setRate] = useState('');
  const [from, setFrom] = useState(today);
  const [reason, setReason] = useState('');

  const next = Number(rate);
  const valid =
    next > 0 &&
    next !== Number(currentRate) &&
    from >= earliest &&
    from <= today &&
    reason.trim().length > 0;

  return (
    <Modal name="Request a day rate change" onClose={onClose} width="460px">
      <div className="space-y-3">
        {blocked && (
          <Callout tone="warning">
            There is already a request waiting. Withdraw it before asking again.
          </Callout>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">New day rate (₹)</label>
            <input
              type="number"
              min="1"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Starts from</label>
            <input
              type="date"
              value={from}
              min={earliest}
              max={today}
              onChange={(e) => setFrom(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
        </div>
        <div className="text-sm text-gray-700">
          {money(currentRate)} → <span className="font-semibold">{next > 0 ? money(next) : '₹—'}</span> a day
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">Reason</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Why the rate should change"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
        <p className="text-xs text-gray-500">
          The owner or the assigned approver decides. Once approved, days from the start date
          are paid at the new rate. The start date can be up to 60 days back.
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700">
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || blocked || !valid}
            onClick={() =>
              onSubmit({ requested_rate: next, effective_from: from, reason: reason.trim() })
            }
            className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
            {busy ? 'Sending…' : 'Send for approval'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

function money(value) {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return '₹0';
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

function dayMonth(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

/** Local calendar day as YYYY-MM-DD (not UTC — that is yesterday before 5:30 am). */
function isoDay(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const Readout = ({ label, value, hint }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    <div className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
      {value || '—'}
    </div>
    {hint && <p className="mt-1 text-[11px] text-gray-500">{hint}</p>}
  </div>
);

const Section = ({ title, hint, children }) => (
  <div>
    <div className="text-sm font-semibold text-gray-800">{title}</div>
    {hint && <p className="text-xs text-gray-500 mb-2">{hint}</p>}
    <div className="mt-2">{children}</div>
  </div>
);

const Grid = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-3">
    {children}
  </div>
);

/**
 * Digits only, at most ten. A pasted "+91 98765 43210" or "098765 43210" keeps
 * the number and drops the country or trunk prefix, rather than keeping the
 * first ten digits and losing the last two.
 */
const phoneDigits = (raw) => {
  let d = String(raw || '').replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('91')) d = d.slice(2);
  else if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
  return d.slice(0, 10);
};

const Input = ({
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  className = '',
  inputMode,
  masked = false,
}) => (
  <div className={className}>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    <input
      type={type}
      value={value ?? ''}
      placeholder={placeholder}
      onChange={onChange}
      inputMode={inputMode}
      // A workman's details, never the signed-in user's. Without this the
      // browser offers saved sign-ins and addresses in these boxes.
      autoComplete="off"
      style={masked ? { WebkitTextSecurity: 'disc' } : undefined}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
    />
  </div>
);

/**
 * The page's head: photograph, name, what he does, and whether he is on the
 * roster at all.
 */
const LabourIdentity = ({ worker, contractorLabel, onSetActive, busy }) => {
  const photo = worker.photo?.url || worker.photos?.[0]?.url || '';
  const active = worker.is_active !== false;
  const meta = [
    worker.trade ? worker.trade.replaceAll('_', ' ') : null,
    worker.skill_level ? worker.skill_level.replaceAll('_', ' ') : null,
    // Decided by sp_id, not by whether a name came back. company_name is blank
    // for most contractors and the detail does not carry the linked user, so
    // falling through to 'In-house' labelled a contractor's man as the builder's.
    worker.sp_id
      ? contractorLabel ||
        worker.sp?.company_name ||
        worker.sp?.user?.name ||
        `Contractor #${worker.sp_id}`
      : 'In-house'
  ].filter(Boolean);

  return (
    <div className="flex flex-wrap items-center gap-4 rounded-lg border border-gray-200 bg-white p-4">
      {photo ? (
        <img
          src={photo}
          alt={worker.name}
          className="h-16 w-16 rounded-full border border-gray-200 object-cover"
        />
      ) : (
        <div className="flex h-16 w-16 items-center justify-center rounded-full border border-dashed border-gray-300 text-lg font-semibold text-gray-400">
          {(worker.name || '?').trim().charAt(0).toUpperCase()}
        </div>
      )}

      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h2 className="truncate text-base font-semibold text-gray-900">
            {worker.name}
          </h2>
          {!active && (
            <span className="rounded bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
              Inactive
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500">{meta.join(' · ')}</p>
        {worker.phone_number && (
          <p className="text-xs text-gray-500">{worker.phone_number}</p>
        )}
      </div>

      <button
        type="button"
        disabled={busy}
        onClick={() => onSetActive(!active)}
        className={`ml-auto rounded-lg border px-3 py-2 text-sm disabled:opacity-50 ${
          active
            ? 'border-gray-300 text-gray-700 hover:bg-gray-50'
            : 'border-brand-700 bg-brand-700 text-white'
        }`}
      >
        {active ? 'Deactivate' : 'Reactivate'}
      </button>
    </div>
  );
};

/**
 * Photograph the card, and let the details come off it.
 *
 * Sits beside the two fields it fills rather than in a section of its own,
 * because it is an easier way to do the typing next to it, not a separate job.
 */
const IdProofCapture = ({ busy, onPick }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">
      Read from the card
    </label>
    <label className="flex cursor-pointer items-center justify-center rounded-lg border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-600 hover:border-brand-600 hover:text-brand-700">
      <input
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        disabled={busy}
        onChange={(e) => {
          onPick(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      {busy ? 'Reading…' : 'Upload Aadhaar or PAN'}
    </label>
    <p className="mt-1 text-[11px] text-gray-500">
      Filed against him either way. Nothing is saved until you agree with it.
    </p>
  </div>
);

/**
 * What the card said, before anybody agrees with it.
 *
 * Deliberately a step rather than an autofill. These fields go onto Form XIII,
 * and a model's reading of a creased Aadhaar is not a good enough reason to
 * put a man's father's name on a statutory register.
 */
const IdProofSuggestion = ({ suggestion, onAccept, onDiscard }) => {
  const rows = [
    ['ID type', suggestion.id_proof_type],
    ['ID number', suggestion.id_proof_number],
    ['Name', suggestion.name],
    ['Father / husband name', suggestion.father_or_husband_name],
    ['Date of birth', suggestion.date_of_birth],
    ['Gender', suggestion.gender],
    ['Permanent address', suggestion.permanent_address]
  ].filter(([, value]) => value);

  return (
    <div className="rounded-lg border border-brand-100 bg-brand-50 p-4">
      <div className="flex flex-wrap items-baseline gap-2">
        <h2 className="text-sm font-semibold text-brand-900">
          Read off the document
        </h2>
        <p className="text-xs text-brand-800">
          Check it against the card in your hand, then fill the form.
        </p>
      </div>

      <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2 xl:grid-cols-3">
        {rows.map(([label, value]) => (
          <div key={label} className="min-w-0">
            <dt className="text-[11px] uppercase tracking-wide text-brand-700">
              {label}
            </dt>
            <dd className="truncate text-sm text-brand-900" title={value}>
              {value}
            </dd>
          </div>
        ))}
      </dl>

      {suggestion.id_proof_masked && (
        <p className="mt-3 text-xs text-brand-800">
          Only the last four digits of an Aadhaar are kept. That is deliberate —
          the register has to show which document was seen, not reproduce it.
        </p>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onAccept}
          className="rounded-lg bg-brand-700 px-3 py-2 text-sm font-medium text-white"
        >
          Fill the form
        </button>
        <button
          type="button"
          onClick={onDiscard}
          className="rounded-lg border border-brand-100 px-3 py-2 text-sm text-brand-900"
        >
          Ignore it
        </button>
      </div>
    </div>
  );
};

/**
 * A document field that takes a file, not a URL.
 *
 * Shows what is already held, so somebody opening a record can see the proof
 * is on file without clicking through to find out.
 */
const FileField = ({ label, name, url, uploading, onPick, onClear }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    {url ? (
      <div className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm">
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="truncate text-brand-700 hover:underline"
          title={name || 'View'}
        >
          {name || 'View'}
        </a>
        <button
          type="button"
          onClick={onClear}
          className="ml-auto shrink-0 text-xs text-gray-500 hover:text-danger-fg"
        >
          Remove
        </button>
      </div>
    ) : (
      <label className="flex cursor-pointer items-center justify-center rounded-lg border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-600 hover:border-brand-600 hover:text-brand-700">
        <input
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          disabled={uploading}
          onChange={(e) => {
            onPick(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
        {uploading ? 'Uploading…' : 'Upload a photo or PDF'}
      </label>
    )}
  </div>
);

/**
 * Who engaged this workman.
 *
 * Blank means in-house — engaged directly by the builder — which is what
 * everybody was before this field existed, so the empty option is the honest
 * default rather than a prompt to choose.
 *
 * It locks once there are wage entries. Every ledger row carries the sp_id the
 * workman had at the time, so moving him afterwards would leave a contractor's
 * settlement holding money for work booked under somebody else.
 */
const ContractorSelect = ({ value, options, locked, onChange }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">
      Engaged through
    </label>
    <select
      value={value ?? ''}
      disabled={locked}
      onChange={onChange}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white disabled:bg-gray-50 disabled:text-gray-500"
    >
      <option value="">In-house (directly by the builder)</option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.label}
        </option>
      ))}
    </select>
    {locked && (
      <p className="mt-1 text-[11px] text-gray-500">
        Locked — he already has wage entries booked under this.
      </p>
    )}
  </div>
);

/**
 * The bank picker.
 *
 * Two groups rather than one long list, so the ten a workman is most likely to
 * bank with are reachable without scrolling. A value already stored that is on
 * neither list is kept as an option, so an existing record shows what it holds
 * rather than rendering blank and looking like the bank was lost.
 */
const BankSelect = ({ value, onChange }) => {
  const known = [...COMMON_BANKS, ...OTHER_BANKS_SORTED];
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-600 mb-1">Bank</span>
      <select
        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
        value={value}
        onChange={(e) => onChange({ target: { value: e.target.value } })}
      >
        <option value="">—</option>
        {value && !known.includes(value) ? (
          <option value={value}>{value}</option>
        ) : null}
        <optgroup label="Commonly used">
          {COMMON_BANKS.map((b) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
        </optgroup>
        <optgroup label="All other banks">
          {OTHER_BANKS_SORTED.map((b) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
        </optgroup>
      </select>
    </label>
  );
};

const Select = ({ label, value, onChange, options }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    <select
      value={value ?? ''}
      onChange={onChange}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
    >
      <option value="">—</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o.replaceAll('_', ' ')}
        </option>
      ))}
    </select>
  </div>
);

function emptyForm() {
  return {
    name: '',
    phone_number: '',
    trade: '',
    skill_level: '',
    sp_id: '',
    ratePerDay: '',
    overtime_rate_per_hour: '',
    gender: '',
    workingHours: '',
    father_or_husband_name: '',
    date_of_birth: '',
    permanent_address: '',
    present_address: '',
    date_of_joining: '',
    date_of_exit: '',
    reason_for_leaving: '',
    transfer_to_project_id: '',
    wage_period: '',
    id_proof_type: '',
    id_proof_number: '',
    uan: '',
    esic_no: '',
    bank_account: '',
    bank_account_confirm: '',
    ifsc: '',
    bank_account_name: '',
    bank_account_type: '',
    bank_name: '',
    bank_proof_url: '',
    bank_proof_name: '',
    projectIds: []
  };
}

function toForm(worker) {
  return {
    ...emptyForm(),
    name: worker.name ?? '',
    phone_number: worker.phone_number ?? '',
    trade: worker.trade ?? '',
    skill_level: worker.skill_level ?? '',
    sp_id: worker.sp_id ? String(worker.sp_id) : '',
    ratePerDay: worker.ratePerDay ?? '',
    overtime_rate_per_hour: worker.overtime_rate_per_hour ?? '',
    gender: worker.gender ?? '',
    workingHours: worker.workingHours ?? '',
    father_or_husband_name: worker.father_or_husband_name ?? '',
    date_of_birth: dateInput(worker.date_of_birth),
    permanent_address: worker.permanent_address ?? '',
    present_address: worker.present_address ?? '',
    date_of_joining: dateInput(worker.date_of_joining),
    date_of_exit: dateInput(worker.date_of_exit),
    reason_for_leaving: worker.reason_for_leaving ?? '',
    wage_period: worker.wage_period ?? '',
    id_proof_type: worker.id_proof_type ?? '',
    id_proof_number: worker.id_proof_number ?? '',
    uan: worker.uan ?? '',
    esic_no: worker.esic_no ?? '',
    bank_account: worker.bank_account ?? '',
    bank_account_confirm: worker.bank_account ?? '',
    ifsc: worker.ifsc ?? '',
    bank_account_name: worker.bank_account_name ?? '',
    bank_account_type: worker.bank_account_type ?? '',
    bank_name: worker.bank_name ?? '',
    bank_proof_url: worker.bank_proof_url ?? '',
    bank_proof_name: worker.bank_proof_name ?? '',
    projectIds: (worker.workerProjectMappings ?? [])
      .map((m) => m.project_id ?? m.project?.id)
      .filter(Boolean)
  };
}

function dateInput(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${`${d.getMonth() + 1}`.padStart(2, '0')}-${`${d.getDate()}`.padStart(2, '0')}`;
}

export default LabourDetail;
